import { useLocation } from "wouter";
import { useRef } from "react";
import auraIconSvg from "@assets/Aura_icon.svg";

export default function Landing() {
  const emailRef = useRef<HTMLInputElement>(null);
  const [, navigate] = useLocation();

  const aiBubbleClass =
    "bg-white rounded-[0_12px_12px_12px] text-[12px] text-eirvana-navy font-sans leading-[1.6] border border-navy/8 shadow-[0_8px_24px_rgba(11,31,58,0.05)]";
  const userBubbleClass =
    "bg-eirvana-navy rounded-[12px_0_12px_12px] text-[12px] text-eirvana-warm font-sans";

  return (
    <div className="font-sans">

      {/* ── Hero ── */}
      <section className="text-center px-8 pt-[48px] pb-[80px] bg-gradient-landing-hero">
        <div className="text-[10px] tracking-[4px] text-navy/40 font-sans mb-7">
          Know your body. Own your health.
        </div>

        <h1 className="text-[clamp(36px,5vw,48px)] font-normal leading-[1.1] text-eirvana-navy mb-7 -tracking-tight">
          We built <span className="text-eirvana-gold italic">Eirvana</span>
          <br />
          because{" "}
          <span className="text-navy/28">"everything</span>
          <br />
          <span className="text-navy/28">looks normal"</span> isn't.
        </h1>

        <p className="text-[15px] leading-[1.8] text-navy/50 font-sans max-w-[460px] mx-auto mb-10">
          Personalized AI health support for women navigating perimenopause and menopause — built
          around your biology, your culture, your life.
        </p>

        <p className="text-[16px] text-eirvana-navy mb-10 italic">
          Zero guessing. Full clarity.
        </p>

        <div className="flex mx-auto max-w-[400px] bg-white/90 rounded-full p-[6px] border border-navy/10 shadow-[0_18px_45px_rgba(11,31,58,0.08)]">
          <input
            ref={emailRef}
            type="email"
            placeholder="your@email.com"
            className="flex-1 bg-transparent border-none outline-none text-[13px] text-eirvana-navy font-sans placeholder-navy/30"
          />
          <button
            type="button"
            onClick={() => {
              const email = emailRef.current?.value?.trim() ?? "";
              const dest = email ? `/register?email=${encodeURIComponent(email)}` : "/register";
              navigate(dest);
            }}
            className="hover:opacity-90 transition-opacity whitespace-nowrap bg-eirvana-navy text-eirvana-gold text-[11px] px-[22px] py-[10px] rounded-full font-sans tracking-[1px] font-medium"
          >
            ADD TO WAITLIST
          </button>
        </div>

        <p className="text-[11px] text-navy/30 font-sans mt-[14px] tracking-[1px]">
          NO SPAM · FOUNDING 100 SPOTS · FREE TO START
        </p>
      </section>

      {/* ── Stats bar ── */}
      <div className="grid grid-cols-3 bg-white/92 border-y border-navy/8 shadow-[0_1px_0_rgba(255,255,255,0.8)_inset]">
        {[
          { n: "1B+", l: "Women postmenopausal by 2025 — most without adequate support" },
          { n: "80%", l: "Experience disruptive symptoms. Only 1 in 5 receive proper care." },
          { n: "4–15", l: "Years women wait before reaching the right support (UCLA I-Corps)" },
        ].map((stat, i) => (
          <div key={i} className={`text-center py-7 px-6 ${i < 2 ? "border-r border-navy/8" : ""}`}>
            <div className="text-[36px] text-eirvana-navy mb-1.5">{stat.n}</div>
            <div className="text-[11px] text-navy/50 leading-[1.6] font-sans">{stat.l}</div>
          </div>
        ))}
      </div>

      {/* ── Cards ── */}
      <section id="how-it-works" className="grid grid-cols-1 md:grid-cols-3 gap-4 px-8 py-10 bg-gradient-landing-cards">
        {[
          {
            title: "No more guessing.",
            body: "Hormonal transitions shouldn't be a black box. We turn your unique biological signals into a clear, personalized roadmap.",
          },
          {
            title: "Your symptoms, decoded.",
            body: 'We connect your lived experience to clinical science. "What is happening to me?" becomes "here is what to do today."',
          },
          {
            title: "Culturally yours.",
            body: "Ayurvedic and Naturopathic tracks built in. The only platform that sees your body through the lens of your culture.",
          },
        ].map((card, i) => (
          <div key={i} className="bg-white/88 rounded-[8px] p-6 border border-navy/8 shadow-[0_18px_45px_rgba(11,31,58,0.06)]">
            <div className="text-[17px] font-medium text-eirvana-navy mb-3 leading-[1.3]">{card.title}</div>
            <div className="text-[12px] text-navy/50 font-sans leading-[1.7]">{card.body}</div>
          </div>
        ))}
      </section>

      {/* ── Quote ── */}
      <section className="text-center px-8 py-[52px] bg-white/90 border-y border-navy/8">
        <p className="text-[20px] leading-[1.7] text-eirvana-navy italic max-w-[520px] mx-auto mb-5">
          "I joined Eirvana after seeing countless women dismissed or misdiagnosed during
          perimenopause in my clinical practice."
        </p>
        <div className="text-[10px] text-eirvana-gold font-sans tracking-[3px]">
          DR. PURVA TRIPATHI · DNB FRCR · BRITISH MENOPAUSE SOCIETY CERTIFIED · NHS CONSULTANT
        </div>
      </section>

      {/* ── Aura section ── */}
      <section id="aura" className="px-8 py-10 bg-gradient-landing-dot text-center">
        <div className="text-[10px] tracking-[4px] text-navy/35 font-sans mb-3">MEET AURA</div>
        <div className="text-[26px] text-eirvana-navy mb-1.5">Your AI companion. Every morning.</div>
        <div className="text-[13px] text-navy/45 font-sans mb-6 max-w-[420px] mx-auto">
          Aura reads your signals and personalizes your entire day around them.
        </div>

        <div className="max-w-lg mx-auto bg-white/88 rounded-[8px] p-5 border border-navy/8 shadow-[0_22px_50px_rgba(11,31,58,0.08)]">
          {/* Chat header */}
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-navy/6">
            <img src={auraIconSvg} alt="Aura" className="h-[30px] w-[30px] shrink-0" />
            <div>
              <div className="text-[13px] text-eirvana-navy">Aura</div>
              <div className="flex items-center gap-1 text-[10px] text-navy/35 font-sans">
                <span className="inline-block rounded-full w-1.5 h-1.5 bg-[#4A9E70]" />
                online now
              </div>
            </div>
          </div>

          {/* AI morning brief card */}
          <div className="mb-2.5">
            <div className={`${aiBubbleClass} p-[12px_14px] inline-block max-w-[95%]`}>
              <div className="text-[10px] text-navy/40 font-sans mb-2.5 tracking-[0.5px]">Good morning — here's your daily brief.</div>
              <div data-state-card className="bg-gradient-to-br from-[#FDF8F0] to-[#FEF5E8] rounded-xl p-3 border border-[rgba(212,120,50,0.18)] mb-2">
                <div className="flex items-center gap-3">
                  <div className="shrink-0">
                    <svg width="64" height="64" viewBox="0 0 88 88">
                      <circle cx="44" cy="44" r="36" fill="none" stroke="#F0EBE0" strokeWidth="7"/>
                      <circle cx="44" cy="44" r="36" fill="none" stroke="#D4813A" strokeWidth="7"
                        strokeLinecap="round" strokeDasharray="226.2" strokeDashoffset="154"
                        transform="rotate(-90 44 44)"
                      />
                      <text x="44" y="49" textAnchor="middle" fontSize="17" fontWeight="600" fill="#0B1F3A" fontFamily="Nunito, sans-serif">32</text>
                    </svg>
                  </div>
                  <div>
                    <div className="text-[9px] tracking-[2px] text-[#D4813A] font-sans mb-0.5 uppercase">Today's State</div>
                    <div className="text-[20px] text-eirvana-navy leading-none mb-2 font-normal">Restore</div>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] bg-[rgba(212,120,50,0.13)] text-[#A85C20] px-[8px] py-[2px] rounded-full font-sans">Low HRV</span>
                      <span className="text-[9px] bg-[rgba(212,120,50,0.13)] text-[#A85C20] px-[8px] py-[2px] rounded-full font-sans">Disrupted sleep</span>
                      <span className="text-[9px] bg-[rgba(212,120,50,0.13)] text-[#A85C20] px-[8px] py-[2px] rounded-full font-sans">Cortisol ↑</span>
                    </div>
                  </div>
                </div>
              </div>
              <span className="text-[11px] text-navy/55">Your body needs rest — not a push. Let's work with that.</span>
            </div>
          </div>

          {/* User message */}
          <div className="text-right mb-2.5">
            <span className={`${userBubbleClass} p-[10px_14px] inline-block max-w-[75%]`}>
              What should I eat?
            </span>
          </div>

          {/* AI message 2 */}
          <div>
            <span className={`${aiBubbleClass} p-[10px_14px] inline-block max-w-[85%]`}>
              On your Ayurveda track, a Restore day calls for Golden Milk — warming,
              anti-inflammatory, hormone-supportive. Want the recipe?
            </span>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="flex justify-between items-center px-8 py-5 bg-white/92 border-t border-navy/8">
        <span className="text-[10px] text-navy/40 font-sans tracking-[1px]">
          © 2025 EIRVANA · HIPAA COMPLIANT · YOUR DATA IS YOURS
        </span>
        <a
          href="mailto:founders@eirvana.one"
          className="no-underline hover:opacity-80 transition-opacity text-[10px] text-navy/40 font-sans tracking-[1px]"
        >
          founders@eirvana.one
        </a>
      </footer>
    </div>
  );
}
